import { Observable, Subscriber } from 'rxjs';
import { WarehouseSyncEventService } from './warehouse-sync-event.service';
import { WarehouseSyncEvent } from '../des.dto';
import { logger, ChangeTyped, Dto, IndexedService } from '@damofire/data-commons';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { Injectable } from '@angular/core';

@Injectable()
export class ObservableSyncService<T extends Dto<any>> {
  constructor(
    protected readonly warehouseSyncEventService: WarehouseSyncEventService,
    protected readonly lockingIndexedService: LockingIndexedService,
    protected readonly indexedService: IndexedService,
  ) {}

  toObservable(store: string, division: string, filter?: (docs: ChangeTyped<T>[]) => ChangeTyped<T>[]): Observable<ChangeTyped<T>[]> {
    return this.toObservableDivisionIndex(store, 'division', division, division, filter);
  }

  toObservableById(store: string, division: string, id: string): Observable<ChangeTyped<T>> {
    return new Observable((observer) => {
      const osub = this.warehouseSyncEventService.watchOutput(division).subscribe((event: WarehouseSyncEvent) => this.notifyObserverEvent(observer, store, id, event));
      this.lockingIndexedService.getByKey<T>(division, store, [division, id]).then((doc) => observer.next(doc));
      observer.add(osub);
    });
  }

  toObservableDivisionIndex(
    store: string,
    index: string,
    key: unknown,
    division: string,
    filter?: (docs: ChangeTyped<T>[]) => ChangeTyped<T>[],
    lock = true,
  ): Observable<ChangeTyped<T>[]> {
    return new Observable((observer) => {
      let previouslyFiltered: ChangeTyped<T>[] = [];
      const osub = this.warehouseSyncEventService.watchOutput(division).subscribe((event) => {
        previouslyFiltered = this.notifyObserverIfStoreMatches(event, store, observer, filter, previouslyFiltered);
      });

      if (lock) {
        this.lockingIndexedService.getAllFromIndex<T>(division, store, index, key).then((docs) => (previouslyFiltered = this.notifyObserver(observer, docs, filter)));
      } else {
        this.indexedService.getAllFromIndex<T>(store, index, key).then((docs) => (previouslyFiltered = this.notifyObserver(observer, docs, filter)));
      }
      observer.add(osub);
    });
  }

  private notifyObserverIfStoreMatches(
    event: WarehouseSyncEvent,
    store: string,
    observer: Subscriber<ChangeTyped<T>[]>,
    filter?: (docs: ChangeTyped<T>[]) => ChangeTyped<T>[],
    previouslyFiltered?: ChangeTyped<T>[],
  ): ChangeTyped<T>[] {
    if (event.store === store) {
      return this.notifyObserver(observer, event.changes, filter, previouslyFiltered);
    }

    return previouslyFiltered;
  }

  private notifyObserver(
    observer: Subscriber<ChangeTyped<T>[]>,
    changes: ChangeTyped<T>[],
    filter?: (docs: ChangeTyped<T>[]) => ChangeTyped<T>[],
    previouslyFiltered?: ChangeTyped<T>[],
  ): ChangeTyped<T>[] {
    const filtered = filter ? filter(changes) : changes;

    if (previouslyFiltered?.length) {
      const toRemove = changes.reduce((p, c) => {
        const noInExpectedData = filtered.findIndex((f) => f._id === c._id) === -1;
        const wasInPreviousFilteredList = previouslyFiltered.findIndex((pf) => pf._id === c._id) !== -1;

        if (noInExpectedData && wasInPreviousFilteredList) {
          p.push({ ...c, changeType: 'removed' });
        }
        return p;
      }, []);

      filtered.forEach((f) => {
        if (previouslyFiltered.findIndex((pf) => pf._id === f._id) === -1) {
          previouslyFiltered.push(f);
        }
      });
      toRemove.forEach((f) => {
        const matchingInx = previouslyFiltered.findIndex((pf) => pf._id === f._id);
        if (matchingInx !== -1) {
          previouslyFiltered.splice(matchingInx, 1);
        }
      });
      observer.next([...filtered, ...toRemove] as ChangeTyped<T>[]);
    } else {
      previouslyFiltered = filtered;
      observer.next(filtered);
    }

    return previouslyFiltered;
  }

  private notifyObserverEvent(observer: Subscriber<ChangeTyped<T>>, store: string, id: unknown, event: WarehouseSyncEvent): void {
    logger.debug('notifyObserverEvent', { id, store, division: event.division, event, category: 'sync' });
    if (event.store === store) {
      const matching = event.changes.find((c) => c._id === id);
      if (matching) {
        observer.next(matching);
      }
    }
  }
}
